import React, { useRef } from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Button, Paper, useTheme, useMediaQuery, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { Icon } from '@iconify/react';
import { useToast } from './ui/ToastProvider';
interface Transaction {
  id: string;
  mtoRef: string;
  bankRef: string;
  mtoName: string;
  remitterName: string;
  beneficiaryName: string;
  beneficiaryIBAN: string;
  amountFCY: string;
  currencyFCY: string;
  amountPKR: string;
  purposeCode: string;
  purposeText: string;
  payoutType: string;
  corridor: string;
  createdAt: string;
  ePRCNo?: string;
  ePRCDate?: string;
}
interface EPRCViewerDrawerProps {
  open: boolean;
  onClose: () => void;
  transaction: Transaction;
}
const EPRCViewerDrawer: React.FC<EPRCViewerDrawerProps> = ({
  open,
  onClose,
  transaction
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    showToast
  } = useToast();
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = () => {
    if (printRef.current) {
      const printWindow = window.open('', '', 'height=600,width=800');
      if (printWindow) {
        printWindow.document.write('<html><head><title>e-PRC Certificate</title>');
        printWindow.document.write('<style>{`');
        printWindow.document.write(`
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0;
            padding: 20mm;
          }
          .certificate-container {
            border: 2px solid #000 !important;
            padding: 30px;
            page-break-inside: avoid;
            page-break-after: always;
          }
          img {
            max-width: 100% !important;
            height: auto !important;
          }
          table { 
            border-collapse: collapse !important; 
            width: 100% !important; 
            margin-bottom: 15px !important;
            border: 1px solid #000 !important;
          }
          td, th { 
            border: 1px solid #000 !important; 
            padding: 8px !important; 
            font-size: 11px !important;
          }
          .header { 
            text-align: center; 
            margin-bottom: 20px; 
            padding-bottom: 15px;
            border-bottom: 2px solid #000 !important;
          }
          .header h1 { 
            font-size: 18px; 
            margin: 0 0 5px 0;
            font-weight: bold;
          }
          .header p { 
            font-size: 13px; 
            margin: 0;
          }
          .section-header {
            background-color: #f5f5f5 !important;
            font-weight: bold !important;
          }
          .note {
            font-size: 10px;
            font-style: italic;
            margin-top: 10px;
          }
          .cert-text {
            font-size: 11px;
            line-height: 1.6;
            margin-bottom: 15px;
          }
          @page {
            size: A4 portrait;
            margin: 0;
          }
          @media print {
            body {
              margin: 0;
              padding: 20mm;
            }
            .certificate-container {
              border: 2px solid #000 !important;
              page-break-after: always;
              page-break-inside: avoid;
            }
            img {
              max-width: 100% !important;
              height: auto !important;
            }
            table, td, th {
              border: 1px solid #000 !important;
            }
            .section-header {
              background-color: #f5f5f5 !important;
            }
          }
        `);
        printWindow.document.write('`}</style></head><body>');
        printWindow.document.write('<div class="certificate-container">');
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.write('</div>');
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      }
    }
  };
  const handleDownload = () => {
    showToast('Use Print button and select "Save as PDF" in the print dialog', 'info', 'Download as PDF', 5000);
    handlePrint();
  };
  // Extract country from corridor (e.g., "USA-PAK" -> "USA")
  const getRemitterCountry = () => {
    const parts = transaction.corridor.split('-');
    return parts[0] || 'N/A';
  };
  // Get country code (simplified - in real app would use proper mapping)
  const getCountryCode = () => {
    const country = getRemitterCountry();
    const codes: Record<string, string> = {
      USA: 'US',
      UAE: 'AE',
      UK: 'GB',
      SAU: 'SA'
    };
    return codes[country] || country;
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: isMobile ? '100%' : 800,
      maxWidth: '100%'
    }
  }} data-id="element-1006" data-mp="1bm0muf3d50xx">
      <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }} data-id="element-1007" data-mp="12l2rsmawk3rh">
        {/* Header */}
        <Box sx={{
        p: 3,
        pb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }} data-id="element-1008" data-mp="1ubgzg0sbojup">
          <Box data-id="element-1009" data-mp="1lj5bclkol8hw">
            <Typography variant="h6" fontWeight={600} data-id="element-1010" data-mp="1lilpwuvto1z4">
              e-PRC Certificate
            </Typography>
            <Typography variant="body2" color="text.secondary" data-id="element-1011" data-mp="1gj98ipnpzn3x">
              {transaction.ePRCNo}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" data-id="element-1012" data-mp="2e1srdmktlxc4">
            <Icon icon="lucide:x" width={20} data-id="element-1013" data-mp="297ya0psotug7" />
          </IconButton>
        </Box>
        <Divider data-id="element-1014" data-mp="2s4q486o0b8jm" />

        {/* e-PRC Content */}
        <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: 4
      }} data-id="element-1015" data-mp="2s6014sj4iaoo">
          <Box ref={printRef} sx={{
          backgroundColor: 'white'
        }} data-id="element-1016" data-mp="2jcwhu3lsr3m4">
            <Box sx={{
            p: 4,
            border: '2px solid #000',
            borderRadius: 0
          }} data-id="element-1017" data-mp="2ne000wrbhbqi">
              {/* Header */}
              <Box className="header" sx={{
              textAlign: 'center',
              mb: 2,
              pb: 2,
              borderBottom: '2px solid #000'
            }} data-id="element-1018" data-mp="2eecvi23ajpt8">
                <Typography component="p" sx={{
                fontSize: '0.75rem',
                textAlign: 'right',
                mb: 1
              }} data-id="element-1019" data-mp="24fxe4qu2fuwx">
                  Appendix V-148
                </Typography>
                <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 1
              }} data-id="element-1020" data-mp="1n6l2e6sjt5wv">
                  <Box component="img" src="https://cdn.magicpatterns.com/uploads/hpRhGyJFvywHpMLciM7oAL/bank-islami-logo.png" alt="BankIslami Logo" sx={{
                  height: 60,
                  width: 'auto'
                }} data-id="element-1021" data-mp="1s5eebus6soux" />
                </Box>
                <Typography component="h1" sx={{
                fontSize: '1.1rem',
                fontWeight: 700,
                mt: 1
              }} data-id="element-1022" data-mp="2fswdibds6p7x">
                  Proceeds Realization Certificate (PRC)
                </Typography>
              </Box>

              {/* e-PRC Number and Date */}
              <TableContainer sx={{
              mb: 2
            }} data-id="element-1023" data-mp="3rqt8jttszlza">
                <Table size="small" sx={{
                border: '1px solid #000'
              }} data-id="element-1024" data-mp="3vrd9l8hhz9qp">
                  <TableBody data-id="element-1025" data-mp="9dv9dp4u4x66">
                    <TableRow data-id="element-1026" data-mp="9ctuf9kp7qj6">
                      <TableCell colSpan={3} sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1,
                      textAlign: 'right'
                    }} data-id="element-1027" data-mp="38sqhzallxgzd">
                        e-PRC No. (Unique ID)
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-1028" data-mp="x5ae1ok33qkl">
                      <TableCell colSpan={3} sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1,
                      textAlign: 'right'
                    }} data-id="element-1029" data-mp="1220sk7g8qe3w">
                        {transaction.ePRCNo}
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-1030" data-mp="27unjrpfg2huj">
                      <TableCell colSpan={3} sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1,
                      textAlign: 'right'
                    }} data-id="element-1031" data-mp="27tcfzpm01a3f">
                        Date of e-PRC:
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-1032" data-mp="3o8dn2tyqp234">
                      <TableCell colSpan={3} sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1,
                      textAlign: 'right'
                    }} data-id="element-1033" data-mp="3o7b0uzcd3ntj">
                        {new Date(transaction.ePRCDate || '').toLocaleDateString('en-GB')}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Remitter and Beneficiary Details */}
              <TableContainer sx={{
              mb: 2
            }} data-id="element-1034" data-mp="3ovdojku4cj62">
                <Table size="small" sx={{
                border: '1px solid #000'
              }} data-id="element-1035" data-mp="7engqiad000e">
                  <TableBody data-id="element-1036" data-mp="3b1dymal23bue">
                    <TableRow data-id="element-1037" data-mp="3b0l202fta2pz">
                      <TableCell colSpan={2} className="section-header" sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-1038" data-mp="363cj1zmu9uno">
                        Remitter Details:
                      </TableCell>
                      <TableCell colSpan={2} className="section-header" sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-1039" data-mp="24c1gokyoq846">
                        Beneficiary Details:
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-1040" data-mp="nybb19rx3bsf">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      width: '25%',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1041" data-mp="nvfdvnnw8r51">
                        Name of Person/ Entity
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      width: '25%',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1042" data-mp="2btgehqht7r1t">
                        {transaction.remitterName}
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      width: '25%',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1043" data-mp="1sxztj1cy1rhh">
                        Name of Person / Entity
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      width: '25%',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1044" data-mp="3luuyhivm8mi">
                        {transaction.beneficiaryName}
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-1045" data-mp="3gwaae023rk6r">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1046" data-mp="3ltk8s57gz66q">
                        NICOP/ CNIC/ Passport No./ POC/ Entity Registration No./
                        Any other Unique ID
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1047" data-mp="2ezmr57y4on6i"></TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1048" data-mp="2j0ybaxy8gg7b">
                        NICOP / CNIC / POC Passport No. /NTN
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1049" data-mp="32onrs90pyu18"></TableCell>
                    </TableRow>
                    <TableRow data-id="element-1050" data-mp="3bn9aq5a3sb2j">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1051" data-mp="3vam739f9lzb2">
                        IBAN/ Bank Account No. (if applicable)
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1052" data-mp="3cknwalk31s3q"></TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1053" data-mp="32o1hoeicnxa5">
                        IBAN/ Bank Account No.
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1054" data-mp="144xyxmrp4lyh">
                        {transaction.beneficiaryIBAN}
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-1055" data-mp="pdxjgxsj7rsu">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1056" data-mp="1n3seuz2wbsg8">
                        Name of Remitting (originating) Financial Institution
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1057" data-mp="1qfwdfk7phw7">
                        {transaction.mtoName}
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1058" data-mp="2decauxa3hhrd">
                        Name of the Bank:
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1059" data-mp="2x10kkg2llv9d"></TableCell>
                    </TableRow>
                    <TableRow data-id="element-1060" data-mp="16pcgjok9blra">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1061" data-mp="16q5q0xalopd4">
                        Name of Country
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1062" data-mp="3nbj3gikvkiq0">
                        {getRemitterCountry()}
                      </TableCell>
                      <TableCell colSpan={2} sx={{
                      border: '1px solid #000'
                    }} data-id="element-1063" data-mp="2qm1t3qay68hz" />
                    </TableRow>
                    <TableRow data-id="element-1064" data-mp="2zkdhdloccu79">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1065" data-mp="jutxaxp7fb0v">
                        Country Code (as per ITRS)
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1066" data-mp="21tqkxhndgp1m">
                        {getCountryCode()}
                      </TableCell>
                      <TableCell colSpan={2} sx={{
                      border: '1px solid #000'
                    }} data-id="element-1067" data-mp="2gjpgdcnjlmde" />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Certification Text */}
              <Box className="cert-text" sx={{
              mb: 2,
              fontSize: '0.75rem',
              lineHeight: 1.5
            }} data-id="element-1068" data-mp="2zffjj377oosv">
                This is to certify that we have received the following amounts
                in favor of the Beneficiary, as per the details given below:
              </Box>

              {/* Transaction Details */}
              <TableContainer sx={{
              mb: 2
            }} data-id="element-1069" data-mp="3bm0i2r9y46mp">
                <Table size="small" sx={{
                border: '1px solid #000'
              }} data-id="element-1070" data-mp="31px5ssldqrm1">
                  <TableBody data-id="element-1071" data-mp="36ql664vi0ksx">
                    <TableRow data-id="element-1072" data-mp="36mnnfkcu1v9k">
                      <TableCell colSpan={5} className="section-header" sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-1073" data-mp="2sq0mz7lo8myw">
                        Transaction Detail:
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-1074" data-mp="2bhhidpn0yphi">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1075" data-mp="2p7b9kem05lue">
                        Date of Realization of Proceeds
                      </TableCell>
                      <TableCell colSpan={4} sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1076" data-mp="2u8ii4fvyyp24">
                        {new Date(transaction.createdAt).toLocaleDateString('en-GB')}{' '}
                        (DD-MM-YYYY)
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-1077" data-mp="3due4j4rpks0a">
                      <TableCell rowSpan={3} sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      verticalAlign: 'top'
                    }} data-id="element-1078" data-mp="3dvgsb3tqiebz">
                        Amount
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-1079" data-mp="m15deju1v7k">
                        Total Proceeds Received in FCY*
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-1080" data-mp="23wedd5s4i7ul">
                        FCY Retained in ESFCA (if applicable)
                      </TableCell>
                      <TableCell colSpan={2} sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-1081" data-mp="2mn5hnsx97824">
                        Converted to PKR
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-1082" data-mp="31kg2hsvsdqai">
                      <TableCell rowSpan={2} sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-1083" data-mp="31j4wdmevc6g8">
                        {transaction.currencyFCY} {transaction.amountFCY}
                      </TableCell>
                      <TableCell rowSpan={2} sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-1084" data-mp="2h5667o9a188m"></TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-1085" data-mp="301edexlunt6k">
                        Amount in FCY
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-1086" data-mp="1qummlz974fo">
                        Rate of Conversion*
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-1087" data-mp="3exml04u81m23">
                        Amt. in PKR
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-1088" data-mp="1394ubh2blkpr">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-1089" data-mp="1b800yxdp5mrl">
                        {transaction.currencyFCY} {transaction.amountFCY}
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-1090" data-mp="rk0rrqys6noa">
                        {(parseFloat(transaction.amountPKR) / parseFloat(transaction.amountFCY)).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-1091" data-mp="drszabej5ive">
                        PKR {parseFloat(transaction.amountPKR).toFixed(2)}
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-1092" data-mp="3ngf1sgvkhlqt">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1093" data-mp="3njay60rro2d5">
                        Purpose of Remittance
                      </TableCell>
                      <TableCell colSpan={4} sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1094" data-mp="34s0b5up5w9qj">
                        {transaction.purposeText}
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-1095" data-mp="seufh2a6onc3">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1096" data-mp="sdt755kjyey2">
                        Purpose Code (as per ITRS)
                      </TableCell>
                      <TableCell colSpan={4} sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1097" data-mp="9iumbo5gzdqx">
                        {transaction.purposeCode}
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-1098" data-mp="3ly6pb379t8cx">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1099" data-mp="3m0j2l3232ujn">
                        Transaction Reference No.
                      </TableCell>
                      <TableCell colSpan={4} sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-1100" data-mp="37ag5s7e0yyco">
                        {transaction.bankRef}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Footer Notes */}
              <Box sx={{
              mb: 2,
              pl: 2
            }} data-id="element-1101" data-mp="8unc3ogrul4k">
                <Box sx={{
                fontSize: '0.7rem',
                mb: 1
              }} data-id="element-1102" data-mp="3shrjqk3y5dub">
                  i. The above transaction has been/will be reported to SBP /
                  SBP-BSC in Schedule ------------- of ITRS for the period:
                  month-------year--------.
                </Box>
                <Box sx={{
                fontSize: '0.7rem',
                mb: 2
              }} data-id="element-1103" data-mp="3ehqih3jrs4eb">
                  ii. The authorized officers of the verifying agency may verify
                  the authenticity of the remittance on our Bank's portal at
                  __________________________.
                </Box>
              </Box>

              <Box className="note" sx={{
              fontSize: '0.7rem',
              fontStyle: 'italic',
              mb: 2
            }} data-id="element-1104" data-mp="2vtbuyq8nv8i9">
                Note: This is a system-generated document and does not require
                any signature.
              </Box>

              <Box sx={{
              fontSize: '0.7rem',
              fontStyle: 'italic',
              mb: 2
            }} data-id="element-1105" data-mp="2go7j0ws3ikdd">
                *N/A in case the payment is received (1) either through Vostro
                account of the remitting financial institution and the rate of
                conversion has been applied by the remitting institution abroad,
                or (2) through Roshan Digital Account.
              </Box>

              <Box sx={{
              textAlign: 'center',
              fontSize: '1rem',
              fontWeight: 600,
              mb: 3
            }} data-id="element-1106" data-mp="22xcfm00d8vh6">
                *****
              </Box>

              {/* Guidance Section */}
              <Box sx={{
              fontSize: '0.7rem',
              mb: 1
            }} data-id="element-1107" data-mp="1f7i2dhc4x4we">
                <Typography sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                textDecoration: 'underline',
                mb: 1
              }} data-id="element-1108" data-mp="175qzqi8h0o8r">
                  Guidance for Authorized Dealers:
                </Typography>
                <Box component="ol" sx={{
                pl: 2,
                m: 0
              }} data-id="element-1109" data-mp="1byakq09hmlnl">
                  <Box component="li" sx={{
                  mb: 0.5
                }} data-id="element-1110" data-mp="j9wmbjehlnbi">
                    The banks should not allow the exporters to buy FCY again
                    for credit to the ESFCAs, if the exporter has voluntarily
                    converted the amount into PKR.
                  </Box>
                  <Box component="li" data-id="element-1111" data-mp="24mzdnc0e3ko9">
                    e-PRC would be issued only once for each incoming
                    remittance.
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Divider data-id="element-1112" data-mp="235akkre3vjwu" />
        <Box sx={{
        p: 3,
        display: 'flex',
        gap: 2
      }} data-id="element-1113" data-mp="235sjjvs5kc8g">
          <Button variant="outlined" startIcon={<Icon icon="lucide:download" width={18} data-id="element-1115" data-mp="2uvoqo1fvnrxy" />} onClick={handleDownload} fullWidth data-id="element-1114" data-mp="2774g6l9g1xq5">
            Download PDF
          </Button>
          <Button variant="contained" startIcon={<Icon icon="lucide:printer" width={18} data-id="element-1117" data-mp="2m33mprgwnpt6" />} onClick={handlePrint} fullWidth data-id="element-1116" data-mp="2zx4chvpvq2he">
            Print
          </Button>
        </Box>
      </Box>
    </Drawer>;
};
export default EPRCViewerDrawer;